import { useTheme } from '@mui/material/styles';
import React from 'react';

import { CurationStatusBadge } from '@components/CurationStatusBadge';
import Icon from '@components/Icon/Icon';
import { RecordSourcePill } from '@components/RecordSourcePill';
import SelfReportedPill from '@components/SelfReportedPill';

import { useSession } from '@hooks/useSession';

import type { CurationStatus, ResourceSourceType } from 'generated/types';

import {
  BottomText,
  InfoTextBox,
  MainText,
  OuterContainer,
  PicBox,
  StatusContainer,
  SubText,
  SelfReportedHeader,
  HorizontalFlex,
  VerticalFlex,
} from './RecordCard.style';

export interface RecordCardProps {
  topText?: string | null;
  text?: string | null;
  bottomText?: string | null;
  color?: string;
  noteDisplay?: boolean | null;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  viewed?: boolean | null;
  coloredSubtext?: boolean | null;
  clickable?: boolean;
  selfReported?: boolean | null;
  secondaryIcon?: React.ReactNode;
  status?: CurationStatus;
  recordSource?: ResourceSourceType;
}

export default function RecordCard({
  topText = '',
  text = '',
  bottomText = '',
  color,
  noteDisplay = false,
  onClick,
  viewed = true,
  coloredSubtext = false,
  clickable = true,
  selfReported = false,
  secondaryIcon,
  status,
  recordSource,
}: RecordCardProps) {
  const {
    state: { isPractitioner, isVitall },
  } = useSession();

  const { palette } = useTheme();

  const resolvedColor = color ?? palette.background.paper;

  function handleChange(event: React.MouseEvent<HTMLDivElement>) {
    if (onClick) {
      onClick(event);
    }
  }

  return (
    <OuterContainer
      clickable={clickable}
      color={resolvedColor}
      data-testid="record-card-container"
      onClick={handleChange}
    >
      {!viewed && <StatusContainer data-testid="record-card-status" />}
      <HorizontalFlex marginBottom={0} marginTop={0}>
        <VerticalFlex alignItems="flex-start" paddingLeft={18}>
          <InfoTextBox>
            <SubText
              color={(coloredSubtext && resolvedColor) || palette.text.primary}
            >
              {topText}
            </SubText>
            <MainText>{text}</MainText>
            <BottomText>{bottomText}</BottomText>
          </InfoTextBox>
        </VerticalFlex>

        <HorizontalFlex justifyContent="flex-end">
          {isVitall ? <CurationStatusBadge status={status} /> : null}

          {isVitall || isPractitioner ? (
            <RecordSourcePill type={recordSource} style={{ mx: 0.5 }} />
          ) : (
            <SelfReportedHeader>
              <SelfReportedPill header value={selfReported ?? false} />
            </SelfReportedHeader>
          )}

          {secondaryIcon ? <PicBox>{secondaryIcon}</PicBox> : null}
          {noteDisplay ? (
            <PicBox>
              <Icon color="text.secondary" name="file-lines" />
            </PicBox>
          ) : null}
          {clickable && onClick ? (
            <PicBox>
              <Icon color="grey.A700" name="chevron-right" size="lg" />
            </PicBox>
          ) : null}
        </HorizontalFlex>
      </HorizontalFlex>
    </OuterContainer>
  );
}
